import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Hook to track page visits for analytics.
 *
 * Reliability notes (this took a few iterations to get right):
 *  - Visitors who bounce within a few hundred ms (a huge slice of landing-page
 *    traffic) were originally losing their visit because we wrapped the POST
 *    in a 300ms setTimeout cancelled by the effect cleanup. That's now removed.
 *  - We previously used `navigator.sendBeacon` with a JSON `Blob`. iOS Safari
 *    < 16 has well-documented silent failures with this combo — it returns
 *    `true` from sendBeacon but never actually fires the request. With a large
 *    iPhone audience that translated to a big undercount.
 *  - Switched to `fetch` with `keepalive: true`. Per the Fetch spec, keepalive
 *    requests are guaranteed to outlive the page (same behaviour as beacon)
 *    but get full fetch error handling and work consistently across modern
 *    Safari, Chrome and Firefox. axios remains as the final fallback for the
 *    rare browser that rejects `keepalive`.
 *  - Dedupe per (pathname × user-presence) so a user object resolving from
 *    null → object doesn't double-count the same view.
 */
const trackKeepalive = async (url, payload) => {
  if (typeof fetch === 'undefined') return false;
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    });
    return resp.ok;
  } catch (_e) {
    return false;
  }
};

export const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  // Remember what we've already recorded so we don't double-count when the
  // user object resolves asynchronously after the route mounts.
  const lastSentRef = useRef('');

  useEffect(() => {
    const fingerprint = `${location.pathname}|${user?.id || 'anon'}`;
    if (lastSentRef.current === fingerprint) return;
    lastSentRef.current = fingerprint;

    const payload = {
      page: location.pathname,
      referrer: document.referrer || '',
      user_id: user?.id || null,
      user_email: user?.email || null,
    };
    const url = `${API_URL}/analytics/visit`;

    // Beacon-replacement: fetch keepalive first, axios fallback. Fire-and-forget.
    trackKeepalive(url, payload).then((ok) => {
      if (!ok) axios.post(url, payload).catch(() => { /* silent */ });
    });
  }, [location.pathname, user?.id]);
};

/**
 * Function to track a product view.
 * Call this when a product detail page loads.
 */
export const trackProductView = async (productId, user = null) => {
  const url = `${API_URL}/analytics/product-view/${productId}`;
  const payload = { user_id: user?.id || null, user_email: user?.email || null };
  const ok = await trackKeepalive(url, payload);
  if (!ok) {
    try { await axios.post(url, payload); } catch (_e) { /* silent */ }
  }
};

export default usePageTracking;
